import { useContext, useEffect, useRef } from "react";

import Flicking from "@egjs/react-flicking";
import { useHistory, useParams } from "react-router-dom";

import Box from "@/components/Box";
import IconType from "@/components/IconType";
import Loading from "@/components/Loading";
import AlertNotificationModal from "@/components/notifications/ui/AlertNotificationModal";
import InterpretationCard from "@/components/reports/InterpretationCard";
import InterpretationTabContent from "@/components/reports/InterpretationTabContent";
import ReportDetailNavigation from "@/components/reports/ReportDetailNavigation";
import CardListContext from "@/contexts/CardListContext";
import { usePersistentState } from "@/contexts/LocalStorage";
import { useNotificationsContext } from "@/contexts/Notification";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  CMP_PATIENTS_REPORT_TAB_URL,
  CMP_PATIENTS_REPORTS_URL,
  CMP_PATIENTS_TEST_RESULTS_URL,
  getCmpUrl
} from "@/core/urls";
import useWindowSize from "@/hooks/use-window-size";
import useCardList from "@/hooks/useCardList";
import Center from "@/tpo/Center";
import ChartModal from "@/tpo/ChartModal";
import Group from "@/tpo/Group";
import SegmentedControl from "@/tpo/SegmentedControl";
import Stack from "@/tpo/Stack";
import { ViewerContext } from "@/tpo/Viewer";
import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

import Template from "../shared/Template";
import ModelContent from "../testResults/ModelContent";
import {
  PATIENT_ENTITLEMENTS_QUERY,
  REPORT_DETAIL_QUERY,
  REPORT_RESULTS_QUERY,
  REPORTS_LIST_QUERY,
  USER_RESULT_INTERPRETATION_DETAILS_QUERY
} from "./graphql/queries";
import OverviewTabContent from "./reportDetail/overview/LockedOverviewTabContent";
import UnlockedOverviewTabContent from "./reportDetail/overview/UnlockedOverviewTabContent";
import SuggestionsTabContent from "./reportDetail/suggestions/SuggestionsTabContent";
import TestsTabContent from "./reportDetail/tests/TestsTabContent";

function ReportDetailContent({
  listQuery,
  detailQuery,
  listQueryVariables,
  skipListQuery,
  detailQueryVariables,
  getReportUrl,
  tabs,
  defaultActiveTab,
  LockedOverviewTabContent,
  UnlockedOverviewTabContent,
  SummaryTabContent,
  InterpretationTabContent,
  SuggestionsTabContent,
  TestsTabContent,
  interpretationQuery,
  interpretationQueryVariables,
  userEntitlements,
  interpretationListCard,
  interpretationListCardQuery,
  interpretationListCardQueryVariables,
  reportListUrl
}) {
  const styledTheme = useTheme();
  const history = useHistory();
  const { reportSlug, tab: tabParam, patientId } = useParams();
  const flickingRef = useRef();
  const { width } = useWindowSize();
  const flickingAlign = width < 450 ? "0" : "center";
  const fallbackTab = defaultActiveTab || tabs?.find(tab => !tab.disabled)?.value || "overview";

  const { organisation } = useOrganisationContext();

  const { data: reportsData, loading: listLoading } = useQuery(listQuery, {
    variables: {
      ...listQueryVariables
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: skipListQuery
  });
  const reports = reportsData?.reports;
  const userReports = reportsData?.userReports;

  const { data: detailData, loading: detailLoading } = useQuery(detailQuery, {
    variables: {
      subsectorSlug: reportSlug,
      ...detailQueryVariables
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    skip: !reportSlug
  });

  const currentReport = detailData?.report;
  const currentUserReport = detailData?.userReport;

  const grantedEntitlements = currentReport?.purchaseProduct?.grantedEntitlements?.map(
    entitlement => entitlement.key
  );
  const isLocked = !userEntitlements
    ?.filter(entitlement => entitlement.granted)
    ?.some(entitlement => grantedEntitlements?.includes(entitlement.key));

  const interpretationTab = tabs.find(tab => tab.value === "interpretation");

  if (isLocked) {
    interpretationTab.disabled = true;
  } else {
    interpretationTab.disabled = false;
  }

  const validTabValues = tabs?.filter(tab => !tab.disabled).map(tab => tab.value) || [];
  const activeTab = validTabValues.includes(tabParam) ? tabParam : fallbackTab;

  useEffect(() => {
    if (!reportSlug) return;
    if (detailData && !isLocked && !detailData.userReport) {
      history.replace(reportListUrl);
    }
    if (tabParam === activeTab) return;

    history.replace(getReportUrl(reportSlug, activeTab));
  }, [activeTab, getReportUrl, history, reportSlug, tabParam, detailData, reportListUrl, isLocked]);

  const notificationApi = useNotificationsContext();

  useEffect(() => {
    if (currentUserReport?.showNewTestMarkersNotification) {
      notificationApi.addNotifications([
        {
          id: `new_test_markers:${reportSlug}`,
          notification: {
            type: `new_test_markers:${reportSlug}`
          }
        }
      ]);
    }
  }, [currentUserReport?.showNewTestMarkersNotification, notificationApi, reportSlug]);

  const [newTestMarkersNotificationAcknowledgedAt, setNewTestMarkersNotificationAcknowledgedAt] =
    usePersistentState(`new_test_markers:${reportSlug}`, { acknowledgedAt: undefined });

  const notification = notificationApi.nextNotification(notification => {
    if (notification.notification.type === `new_test_markers:${reportSlug}`) {
      if (
        newTestMarkersNotificationAcknowledgedAt.acknowledgedAt == undefined ||
        newTestMarkersNotificationAcknowledgedAt.acknowledgedAt < Date.now() - 24 * 60 * 60 * 1000
      ) {
        return true;
      }
    }
    return false;
  });

  if (listLoading || detailLoading || !reports?.length) return <Loading />;

  if (!currentReport) return null;

  return (
    <>
      <Group
        flexDirection={["column", "column", "row"]}
        justifyContent={["space-between", "space-between", "center"]}
        pt={styledTheme.spacing.section.pt}
        pb={styledTheme.spacing.section.pb}
        maxWidth={1648}
        mx="auto"
        gap={[20, 20, 40]}
      >
        <ReportDetailNavigation
          reports={userReports
            ?.filter(ur => {
              const grantedEntitlements = ur.subsector.purchaseProduct?.grantedEntitlements?.map(
                entitlement => entitlement.key
              );
              return userEntitlements
                ?.filter(entitlement => entitlement.granted)
                ?.some(userEntitlement => grantedEntitlements?.includes(userEntitlement.key));
            })
            ?.map(ur => ur.subsector)}
          currentSlug={reportSlug}
          getReportUrl={getReportUrl}
          activeTab={activeTab}
        />
        <Center stacked gap={[20, 20, 40]} maxWidth={1024} flexGrow={1} minWidth={0}>
          <Stack gap={20}>
            <Group gap={20} alignItems="center" flexDirection={["column", "column", "row"]}>
              <IconType
                iconType={currentReport?.purchaseProduct?.content?.reportIcon}
                iconSize={80}
                withCircle
              />
              <Box
                fontFamily="gilroyBold"
                fontSize={[40, 40, 68]}
                textAlign={["center", "center", "left"]}
              >
                {currentReport.name}
              </Box>
            </Group>
            {/* {currentReport.content.shortDescription && (
            <Box
              fontFamily="gilroyRegular"
              fontSize={[18, 18, 24]}
              lineHeight={["28px", "28px", "36px"]}
              maxWidth={720}
              mx="auto"
            >
              {currentReport.content.shortDescription}
            </Box>
          )} */}
          </Stack>
          <Box width="100%" minWidth={0} overflow="hidden">
            <Flicking
              align={flickingAlign}
              key={width}
              bound
              moveType="freeScroll"
              ref={flickingRef}
              style={{ paddingLeft: 20, paddingRight: 20 }}
            >
              <SegmentedControl
                data={tabs}
                value={activeTab}
                onChange={nextTab => {
                  if (!reportSlug || nextTab === activeTab) return;
                  history.push(getReportUrl(reportSlug, nextTab));
                }}
                bg="white"
                size={["xs", "xs", "sm"]}
              />
            </Flicking>
          </Box>
          <Box px={[16, 16, 0]} width="100%">
            {activeTab === "overview" ? (
              isLocked ? (
                <LockedOverviewTabContent
                  report={currentReport}
                  markersAssessed={currentReport?.assessedDatapoints || []}
                />
              ) : (
                <UnlockedOverviewTabContent report={currentReport} userReport={currentUserReport} />
              )
            ) : activeTab === "summary" ? (
              <SummaryTabContent report={currentReport} />
            ) : activeTab === "interpretation" ? (
              <InterpretationTabContent
                report={currentReport}
                reportResultsQuery={interpretationQuery}
                reportResultsQueryVariables={interpretationQueryVariables}
                ListCard={interpretationListCard}
                listCardQuery={interpretationListCardQuery}
                listCardQueryVariables={interpretationListCardQueryVariables}
              />
            ) : activeTab === "suggestions" ? (
              <SuggestionsTabContent report={currentReport} />
            ) : activeTab === "tests" && TestsTabContent ? (
              <TestsTabContent report={currentReport} userReport={currentUserReport} />
            ) : (
              <Box
                bg="white"
                borderRadius={styledTheme.radius.card}
                p={[20, 20, 40]}
                minHeight={200}
              >
                <Box fontFamily="gilroyBold" fontSize={[18, 18, 24]} textTransform="capitalize">
                  {activeTab}
                </Box>
              </Box>
            )}
          </Box>
        </Center>
      </Group>
      {notification && (
        <AlertNotificationModal
          onClose={() => {
            setNewTestMarkersNotificationAcknowledgedAt({ acknowledgedAt: Date.now() });
          }}
          onButtonClick={() => {
            setNewTestMarkersNotificationAcknowledgedAt({ acknowledgedAt: Date.now() });
            history.push(
              getCmpUrl(CMP_PATIENTS_TEST_RESULTS_URL, {
                organisationId: organisation.hashId,
                patientId
              })
            );
          }}
        />
      )}
    </>
  );
}

function PatientReportDetailContent() {
  const { patientId, reportSlug } = useParams();
  const { organisationId, organisation } = useOrganisationContext();
  const { userId } = useContext(ViewerContext);

  const { data: entitlementsData } = useQuery(PATIENT_ENTITLEMENTS_QUERY, {
    variables: {
      userId,
      organisationId
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !organisationId || !userId
  });

  const { cardOpen, setCardOpen, viewCard } = useCardList();

  const TABS = [
    { value: "overview", label: "Overview" },
    { value: "summary", label: "Summary", disabled: true },
    { value: "interpretation", label: "Interpretation" },
    { value: "suggestions", label: "Suggestions", disabled: true },
    { value: "tests", label: "Tests" }
  ];

  return (
    <CardListContext.Provider
      value={{
        viewCard
      }}
    >
      <ReportDetailContent
        listQuery={REPORTS_LIST_QUERY}
        detailQuery={REPORT_DETAIL_QUERY}
        listQueryVariables={{
          organisationId,
          userId
        }}
        skipListQuery={!organisationId || !userId}
        detailQueryVariables={{
          organisationId,
          slug: reportSlug,
          userId
        }}
        reportListUrl={getCmpUrl(CMP_PATIENTS_REPORTS_URL, {
          organisationId: organisation.hashId,
          patientId
        })}
        getReportUrl={(reportSlug, tab = "overview") =>
          getCmpUrl(CMP_PATIENTS_REPORT_TAB_URL, {
            organisationId: organisation.hashId,
            patientId,
            reportSlug,
            tab
          })
        }
        tabs={TABS}
        UnlockedOverviewTabContent={UnlockedOverviewTabContent}
        SummaryTabContent={() => null}
        InterpretationTabContent={InterpretationTabContent}
        interpretationQuery={REPORT_RESULTS_QUERY}
        interpretationQueryVariables={{
          organisationId,
          subsectorSlug: reportSlug,
          userId
        }}
        interpretationListCard={InterpretationCard}
        interpretationListCardQuery={USER_RESULT_INTERPRETATION_DETAILS_QUERY}
        interpretationListCardQueryVariables={{
          organisationId,
          userId
        }}
        SuggestionsTabContent={SuggestionsTabContent}
        TestsTabContent={TestsTabContent}
        LockedOverviewTabContent={OverviewTabContent}
        userEntitlements={entitlementsData?.user?.entitlements}
      />
      <ChartModal close={() => setCardOpen(undefined)} show={!!cardOpen?.type}>
        <ModelContent type={cardOpen?.type} item={cardOpen?.item} />
      </ChartModal>
    </CardListContext.Provider>
  );
}

export default function PatientReportDetail({ templateProps }) {
  return (
    <Template selectedTab="reports" docTitle="Report detail" {...templateProps}>
      <PatientReportDetailContent />
    </Template>
  );
}
